import { BroadcastBanner } from "@/components/broadcast-banner";
import { NotificationsButton } from "@/components/notifications";
import {
  Breadcrumb,
  BreadcrumbItem,
  // BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import { SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Fragment } from "react";
import { AppSidebar } from "@/components/sidebar/app-sidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Outlet } from "react-router-dom";
import { useLayoutStore } from "@/store";
import { PrintQueueProvider } from "./garage-register/print-queue-context";
import { PrintQueueBadge } from "./garage-register/_components/print-queue-badge";

interface IBreadcrumbItem {
  href: string;
  label: string;
  isLast: boolean;
}

const generateBreadcrumbs = (
  path: string,
  searchParams: URLSearchParams
): IBreadcrumbItem[] => {
  const parts = path.split("/").filter((part) => part);
  const items: IBreadcrumbItem[] = parts.map((part, index) => {
    const href = `/${parts.slice(0, index + 1).join("/")}`;
    const isLast = index === parts.length - 1;

    return {
      href,
      label: part
        .replace(/-/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase()),
      isLast: isLast && !searchParams.get("id"), // Only last if no ID in params
    };
  });

  // Add detail page to breadcrumb if ID exists
  const id = searchParams.get("id");
  if (id) {
    items.push({
      href: `${items[items.length - 1].href}?id=${id}`,
      // label: `${id}`,
      label: "Details",
      isLast: true,
    });
  }

  return items;
};

export default function AppsLayout() {
  const pathname = useLocation().pathname;
  const [searchParams] = useSearchParams();
  const crumbs = generateBreadcrumbs(pathname, searchParams);
  const { sidebarState, setSidebarState } = useLayoutStore();
  const toggleSidebarState = () =>
    setSidebarState(sidebarState === "collapsed" ? "expanded" : "collapsed");

  return (
    <PrintQueueProvider>
      <SidebarProvider open={sidebarState === "expanded"}>
        <AppSidebar className="bg-white" />
        <SidebarInset className="flex-1 flex flex-col overflow-hidden">
          <header
            className="flex px-3 mt-2 h-[45px] shrink-0 items-center gap-2 transition-[width,height] ease-linear 
                group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12 w-full md:pl-0"
          >
            <SidebarTrigger onClick={toggleSidebarState} className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                {crumbs.map((item) => (
                  <Fragment key={item.href}>
                    <BreadcrumbItem className="hidden md:block">
                      {!item.isLast ? (
                        <Link to={item.href}>{item.label}</Link>
                      ) : (
                        <BreadcrumbPage>{item.label}</BreadcrumbPage>
                      )}
                    </BreadcrumbItem>
                    {!item.isLast && (
                      <BreadcrumbSeparator className="hidden md:block" />
                    )}
                  </Fragment>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
            <BroadcastBanner />
            <NotificationsButton />
          </header>
          <div className="flex-1 max-w-full pr-3 pb-3 pl-3 h-[calc(100%-56px)] md:pl-0">
            <Outlet />
          </div>
        </SidebarInset>
        <PrintQueueBadge />
      </SidebarProvider>
    </PrintQueueProvider>
  );
}
